import { Contrast, Hct, argbFromHex, themeFromSourceColor } from "@material/material-color-utilities";

import { Controller } from "@hotwired/stimulus";
import { defineWebinarTheme, resetWebinarTheme } from "~/themes/webinar_theme";

// 4.5 is the minimum contrast ratio for text following WCAG AA standards.
const MINIMUM_CONTRAST_RATIO = 4.5;

const THEMES = ["light", "dark"];
const COLOR_TYPES = ["accent", "secondary"];

const interpolate = (template, values) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`%{${key}}`, value),
    template,
  );

export default class extends Controller {
  static targets = ["themeToggleNotice", "exactColorsDisabledNotice", "exactColorsDescription", "exactColorsEnabledNotice", "contrastAccentWarning", "contrastSecondaryWarning"];
  static values = { contrastConfig: Object };

  connect() {
    // Saving the form triggers a Turbo morph refresh, which re-renders the
    // checkboxes to their saved state without reconnecting this controller.
    // Re-sync after every morph so the disabled states, notices and previews match.
    this.boundSync = this.sync.bind(this);
    document.addEventListener("turbo:morph", this.boundSync);
    this.sync();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.boundSync);
  }

  enable(event) {
    // While custom colors were off the exact colors setting was hidden, so a
    // theme toggle enabled in the meantime wins over a lingering checkmark.
    if (event.target.checked && this.themeToggle?.checked) this.forceExactColorsOff();
    this.sync();
  }

  enableExact() {
    this.sync();
  }

  toggleExclusive(event) {
    if (event.target.checked) this.forceExactColorsOff();
    this.sync();
  }

  // The webinar theme toggle and exact theme colors are mutually exclusive,
  // but exact colors only take effect while custom theme colors are on: the
  // setting is hidden otherwise, so it must not block the theme toggle.
  sync() {
    const exactColorsApplied = Boolean(this.exactColors?.checked && this.customColors?.checked);
    const themeToggleOn = Boolean(this.themeToggle?.checked);

    if (this.themeToggle) this.themeToggle.disabled = exactColorsApplied;
    if (this.exactColors) this.exactColors.disabled = themeToggleOn || this.customColorsLocked;

    if (this.hasThemeToggleNoticeTarget) this.themeToggleNoticeTarget.hidden = !exactColorsApplied;
    if (this.hasExactColorsEnabledNoticeTarget) this.exactColorsEnabledNoticeTarget.hidden = !exactColorsApplied;
    if (this.hasExactColorsDisabledNoticeTarget) {
      this.exactColorsDisabledNoticeTarget.hidden = !themeToggleOn;
      this.exactColorsDescriptionTarget.hidden = themeToggleOn;
    }

    this.refresh();
  }

  refresh() {
    this.checkContrast();
    this.renderPreviews();
  }

  forceExactColorsOff() {
    if (!this.exactColors?.checked) return;

    this.exactColors.checked = false;
  }

  get customColors() {
    return document.getElementById("branding_theme_colors_active");
  }

  // Accounts without the custom colors feature get the whole premium section
  // disabled server side, so it must never be re-enabled by a sync.
  get customColorsLocked() {
    return Boolean(this.customColors?.disabled);
  }

  get themeToggle() {
    return document.getElementById("branding_webinar_theme_toggle");
  }

  get webinarTheme() {
    return document.getElementById("branding_webinar_theme");
  }

  get exactColors() {
    return document.getElementById("branding_apply_exact_theme_colors");
  }

  update() {
    this.refresh();
  }

  // Both previews sit in their own webinar theme region, so registering the
  // form's colors as the webinar themes restyles them at once, the same way
  // the viewer facing pages pick up the saved branding.
  renderPreviews() {
    if (this.customThemeApplies) {
      defineWebinarTheme({
        accent: this.colorValue("accent"),
        secondary: this.colorValue("secondary"),
        exact: Boolean(this.exactColors?.checked),
      });
    } else {
      resetWebinarTheme();
    }
  }

  get customThemeApplies() {
    return Boolean(this.customColors?.checked) && COLOR_TYPES.every((type) => this.isValidHex(this.colorValue(type)));
  }

  checkContrast() {
    if (!this.hasContrastAccentWarningTarget && !this.hasContrastSecondaryWarningTarget) return;

    this.renderNotices(this.contrastResult());
  }

  // Reports the accessibility contrast check for the exact accent and secondary
  // colors: for each color, the themes in which it is hard to see. Only themes
  // a viewer can actually reach are checked. Generated (non-exact) themes
  // always keep enough contrast, so the check only runs when exact colors
  // apply. Returns null when nothing to show.
  contrastResult() {
    if (!this.contrastCheckApplies) return null;

    const colorsByType = {
      accent: this.colorValue("accent"),
      secondary: this.colorValue("secondary"),
    };

    if (!Object.values(colorsByType).every((color) => this.isValidHex(color))) {
      return null;
    }

    // Surface is derived from the accent color (the theme's source color) for
    // both the light and dark schemes; the secondary color does not affect it.
    const exactColorTheme = themeFromSourceColor(argbFromHex(colorsByType.accent));
    const reachableThemes = this.reachableThemes();
    const failingThemesByType = Object.fromEntries(
      COLOR_TYPES.map((type) => {
        const colorTone = Hct.fromInt(argbFromHex(colorsByType[type])).tone;
        const failing = reachableThemes.filter((theme) => {
          const surfaceTone = Hct.fromInt(exactColorTheme.schemes[theme].surface).tone;
          return Contrast.ratioOfTones(colorTone, surfaceTone) < MINIMUM_CONTRAST_RATIO;
        });
        return [type, failing];
      }),
    );

    if (Object.values(failingThemesByType).every((themes) => themes.length === 0)) return null;

    return failingThemesByType;
  }

  // Themes a viewer can reach with the current form settings: both themes when
  // the theme toggle is on, otherwise just the selected webinar theme. Reading
  // the live inputs keeps the notice in sync while the user edits the form,
  // since the webinar theme settings and the colors live in the same form.
  reachableThemes() {
    if (!this.themeToggle || !this.webinarTheme) return THEMES;

    return this.themeToggle.checked ? THEMES : [this.webinarTheme.value];
  }

  renderNotices(failingThemesByType) {
    this.renderNotice(this.contrastAccentWarningTarget, failingThemesByType?.accent ?? []);
    this.renderNotice(this.contrastSecondaryWarningTarget, failingThemesByType?.secondary ?? []);
  }

  renderNotice(target, failingThemes) {
    const message = this.contrastWarning(failingThemes);

    target.textContent = message;
    target.hidden = !message;
  }

  contrastWarning(failingThemes) {
    if (failingThemes.length === 0) return "";
    if (failingThemes.length === THEMES.length) return this.contrastConfigValue.warningBoth;

    return interpolate(this.contrastConfigValue.warningSingle, {
      theme: this.contrastConfigValue.themes[failingThemes[0]],
    });
  }

  // The check only makes sense when a custom theme is enabled and exact colors
  // are being applied.
  get contrastCheckApplies() {
    return Boolean(this.customColors?.checked && this.exactColors?.checked);
  }

  colorValue(type) {
    const input = this.element.querySelector(`input[type="text"][data-type="${type}"]`);
    const colorInput = this.element.querySelector(`input[type="color"][data-type="${type}"]`);
    return input?.value || colorInput?.value || "";
  }

  isValidHex(value) {
    return /^#[0-9a-fA-F]{6}$/.test(value);
  }
}
